import { useEffect, useState } from 'react';
import { Handshake, Save, Loader2, MessageSquareOff } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { ParceiroFuncionamento } from './ParceiroFuncionamento';

// PARCEIRO FIO DE COBRE — os parametros do programa + o acompanhamento (DA-445).
//
// As tres variaveis ja existiam em `app_settings` desde a DA-435; o que nao existia era um lugar
// pra olhar e mexer nelas sem abrir o banco. A parte de baixo da tela nao e enfeite: o programa
// aposta que o que prende o parceiro e' SALDO PARADO aqui, e ate agora esse numero — o unico que
// diz se a aposta esta de pe — nao aparecia em lugar nenhum.

interface Resumo {
  vendas: number;
  parceiros: number;
  parceiros_com_venda: number;
  markup_total: string;
  custo_operacao_total: string;
  credito_gerado: string;
  saldo_parado: string;
  gasto: string;
  markup_min: string;
  markup_max: string;
  markup_mediana: string;
}

interface Parceiro {
  id: number;
  nome: string;
  whatsapp: string | null;
  codigo_legado: string | null;
  vendas: number;
  credito_gerado: string;
  markup_total: string;
  ultima_venda: string | null;
  saldo: string;
  gasto: string;
}

type Aba = 'geral' | 'funcionamento';
// A aba escolhida persiste por dispositivo, como os demais filtros de tela do sistema.
const CHAVE_ABA = 'fdc_parceiro_aba';

const CHAVE_CUSTO = 'programa_prestador_custo_operacao_pct';
const CHAVE_TETO_MARKUP = 'programa_prestador_teto_markup_pct';
const CHAVE_TETO_USO = 'programa_prestador_teto_uso_pct';

export function ParceiroPrograma() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [custo, setCusto] = useState('15');
  const [tetoMarkup, setTetoMarkup] = useState('');
  const [tetoUso, setTetoUso] = useState('30');

  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [parceiros, setParceiros] = useState<Parceiro[]>([]);

  const [aba, setAba] = useState<Aba>(() =>
    (localStorage.getItem(CHAVE_ABA) === 'funcionamento' ? 'funcionamento' : 'geral'));
  useEffect(() => { localStorage.setItem(CHAVE_ABA, aba); }, [aba]);

  useEffect(() => {
    let cancelado = false;
    async function carregar() {
      setLoading(true);
      try {
        const ler = (chave: string) =>
          api.get<{ valor: string | null }>(`/api/app-settings/${chave}`).catch(() => ({ valor: null }));
        const [c, tm, tu, r, p] = await Promise.all([
          ler(CHAVE_CUSTO),
          ler(CHAVE_TETO_MARKUP),
          ler(CHAVE_TETO_USO),
          api.get<Resumo>('/api/programa/resumo'),
          api.get<Parceiro[]>('/api/programa/parceiros'),
        ]);
        if (cancelado) return;
        if (c?.valor != null) setCusto(c.valor);
        setTetoMarkup(tm?.valor ?? '');
        if (tu?.valor != null) setTetoUso(tu.valor);
        setResumo(r);
        setParceiros(p);
      } catch (e: any) {
        toast.erro(e.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    void carregar();
    return () => { cancelado = true; };
  }, [toast]);

  // Valida os TRES antes de gravar qualquer um: sao um conjunto, e salvar dois e recusar o
  // terceiro deixaria o programa numa combinacao que ninguem escolheu.
  function validar(): string | null {
    const c = Number(custo.replace(',', '.'));
    if (!Number.isFinite(c) || c < 0 || c > 100) return 'Custo da Operacao deve ficar entre 0 e 100%.';
    const tu = Number(tetoUso.replace(',', '.'));
    if (tetoUso.trim() !== '' && (!Number.isFinite(tu) || tu < 0 || tu > 100)) {
      return 'Teto de uso deve ficar entre 0 e 100%, ou vazio para sem limite.';
    }
    if (tetoMarkup.trim() !== '') {
      const tm = Number(tetoMarkup.replace(',', '.'));
      if (!Number.isFinite(tm) || tm <= 0) return 'Teto de markup deve ser um percentual maior que zero, ou vazio para sem limite.';
    }
    return null;
  }

  async function salvar() {
    const erro = validar();
    if (erro) { toast.erro(erro); return; }
    setSalvando(true);
    try {
      await Promise.all([
        api.put(`/api/app-settings/${CHAVE_CUSTO}`, { valor: custo.trim().replace(',', '.') }),
        api.put(`/api/app-settings/${CHAVE_TETO_MARKUP}`, { valor: tetoMarkup.trim().replace(',', '.') }),
        api.put(`/api/app-settings/${CHAVE_TETO_USO}`, { valor: tetoUso.trim().replace(',', '.') }),
      ]);
      toast.sucesso('Parametros do Parceiro FIODECOBRE atualizados');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const labelInput = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 } as const;
  const campo = { padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13, background: 'var(--input-bg, #fff)' } as const;
  const ajuda = { fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 } as const;
  const cartao = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 } as const;

  const semTeto = tetoMarkup.trim() === '';
  const dt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <Handshake size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Parceiro FIODECOBRE
        </h2>
      </div>

      <div className="abas">
        <button
          type="button"
          className={`aba-btn ${aba === 'geral' ? 'aba-btn-ativo' : ''}`}
          onClick={() => setAba('geral')}
        >
          Geral
        </button>
        <button
          type="button"
          className={`aba-btn ${aba === 'funcionamento' ? 'aba-btn-ativo' : ''}`}
          onClick={() => setAba('funcionamento')}
        >
          Funcionamento
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> Carregando...
        </div>
      ) : aba === 'funcionamento' ? (
        // Recebe os parametros VIVOS: o documento se contradizer a aba ao lado e' pior que nao existir.
        <ParceiroFuncionamento custoPct={custo} tetoMarkupPct={tetoMarkup} tetoUsoPct={tetoUso} />
      ) : (
        // Duas colunas: os parametros (que se mexe de vez em quando) a esquerda, estreitos, e o
        // acompanhamento (que se OLHA) ocupando a largura que sobrava vazia. Cabe sem rolar a tela.
        <div className="parceiro-grid">
          <div style={cartao}>
            <Secao titulo="Parametros" />
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={labelInput}>Custo da Operacao (%)</label>
                <input value={custo} onChange={(e) => setCusto(e.target.value)} style={{ ...campo, width: '100%' }} />
                <div style={ajuda}>Quanto a loja retem da diferenca.</div>
              </div>

              <div>
                <label style={labelInput}>Teto de markup (%)</label>
                <input
                  value={tetoMarkup}
                  onChange={(e) => setTetoMarkup(e.target.value)}
                  placeholder="vazio = sem limite"
                  style={{ ...campo, width: '100%' }}
                />
                <div style={ajuda}>
                  Passou do teto, o pedido <b>nao vai ao caixa</b>.
                  {semTeto && (
                    // Etiqueta em linha propria e sem quebra: partida ao meio ("Hoje: sem / limite")
                    // ela deixa de parecer um selo e vira texto solto.
                    <div style={{ marginTop: 4 }}>
                      <span style={{
                        background: '#fde68a', color: '#000', padding: '1px 6px',
                        borderRadius: 3, fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                        Hoje: sem limite
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={labelInput}>Teto de uso do credito (%)</label>
                <input value={tetoUso} onChange={(e) => setTetoUso(e.target.value)} style={{ ...campo, width: '100%' }} />
                <div style={ajuda}>Quanto do credito cabe numa compra. <b>0 suspende</b> o gasto.</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button
                type="button"
                className="btn-primario"
                onClick={() => void salvar()}
                disabled={salvando}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salvar
              </button>
            </div>

            {/* A explicacao longa saiu daqui: ela agora e a aba Funcionamento inteira, e repetir um
                resumo dela aqui so gastaria a altura que o Ruan quer de volta. Fica o que NAO esta
                la e so importa na hora de mexer: o ajuste nao retroage. */}
            <div style={{ ...ajuda, marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              Mudanca vale <b>da proxima venda em diante</b> — o que ja foi atribuido fica congelado.
              O programa inteiro esta explicado na aba <b>Funcionamento</b>.
            </div>
          </div>

          <div style={{ display: 'grid', gap: 16, alignContent: 'start', minWidth: 0 }}>
            {resumo && (
              <div style={cartao}>
                <Secao titulo="Como o programa esta andando" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                  <Numero rotulo="Saldo parado" valor={formatMoeda(resumo.saldo_parado)} destaque
                    nota="o que ainda traz o parceiro de volta" />
                  <Numero rotulo="Credito gerado" valor={formatMoeda(resumo.credito_gerado)} nota={`em ${resumo.vendas} venda(s)`} />
                  <Numero rotulo="Credito gasto" valor={formatMoeda(resumo.gasto)} nota="ja voltou como mercadoria" />
                  <Numero rotulo="Custo da Operacao" valor={formatMoeda(resumo.custo_operacao_total)} nota="retido pela loja" />
                  <Numero rotulo="Parceiros" valor={String(resumo.parceiros)} nota={`${resumo.parceiros_com_venda} ja venderam`} />
                </div>
              </div>
            )}

          <div className="parceiro-linha2">
          <div style={cartao}>
            <Secao titulo={`Quem esta no programa (${parceiros.length})`} />
            {parceiros.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Ninguem marcado ainda. A adesao se marca na ficha do cliente, em Arquivos &rarr; Clientes.
              </div>
            ) : (
              // Rola por DENTRO quando a lista crescer — a pagina em si continua cabendo na tela.
              <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
                <table className="tabela" style={{ width: '100%', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Codigo</th>
                      <th style={{ textAlign: 'left' }}>Parceiro</th>
                      <th style={{ textAlign: 'right' }}>Vendas</th>
                      <th style={{ textAlign: 'right' }}>Gerado</th>
                      <th style={{ textAlign: 'right' }}>Gasto</th>
                      <th style={{ textAlign: 'right' }}>Saldo parado</th>
                      <th style={{ textAlign: 'right' }}>Ultima venda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parceiros.map((p) => (
                      <tr key={p.id}>
                        {/* Mesma peca da lista de Clientes (`.codigo-curto`): o iOS Safari transforma
                            numero curto em link de telefone se ele for texto solto. */}
                        <td><span className="codigo-curto">{p.codigo_legado ?? '—'}</span></td>
                        <td>
                          {p.nome}
                          {/* O selo exige WhatsApp (DA-437), mas quem foi marcado ANTES da trava pode
                              estar sem — e ai o saldo existe e o parceiro nao tem como consultar. */}
                          {!p.whatsapp && (
                            <span
                              title="Sem WhatsApp no cadastro — ele nao consegue consultar o saldo digitando 'extrato'"
                              style={{ marginLeft: 6, color: '#f97316', verticalAlign: 'middle', display: 'inline-flex' }}
                            >
                              <MessageSquareOff size={13} />
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{p.vendas}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoeda(p.credito_gerado)}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoeda(p.gasto)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(p.saldo)}</td>
                        <td style={{ textAlign: 'right' }}>{dt(p.ultima_venda)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* A distribuicao do markup aparece SEMPRE, inclusive vazia — ela e o instrumento que
              decide o numero do teto, e um instrumento que so aparece quando ja ha dado nao ensina
              ninguem a esperar por ele (principio 1: recurso invisivel nao existe). */}
          {resumo && (
            <div style={{ ...cartao, alignSelf: 'stretch' }}>
              <Secao titulo="Markup praticado" />
              {resumo.vendas > 0 ? (
                <>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <Faixa rotulo="Minimo" valor={`${resumo.markup_min}%`} />
                    <Faixa rotulo="Mediana" valor={`${resumo.markup_mediana}%`} destaque />
                    <Faixa rotulo="Maximo" valor={`${resumo.markup_max}%`} />
                  </div>
                  <div style={{ ...ajuda, marginTop: 12 }}>
                    E daqui que sai o numero do <b>teto de markup</b>. Com poucas vendas ele ainda nao diz
                    nada — vale medir depois de 1 a 2 meses.
                  </div>
                </>
              ) : (
                <div style={ajuda}>
                  Nenhuma venda do programa ainda. Quando houver, esta caixa mostra o quanto os parceiros
                  costumam inflar — <b>e e desse numero que sai o teto de markup</b>, nunca de palpite.
                  <br /><br />
                  ⚠️ Percentual morde muito mais no pedido pequeno: uma compra de R$ 4,00 fechada em R$ 5,00
                  ja e 25%.
                </div>
              )}
            </div>
          )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Rotulo de secao: maiusculas pequenas com espacamento, em vez de um <h3> do tamanho do titulo da
// tela. Separa os blocos sem competir com o `.pagina-titulo`, que e quem nomeia a pagina.
function Secao({ titulo }: { titulo: string }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
      color: 'var(--text-muted)', marginBottom: 12,
      paddingBottom: 6, borderBottom: '1px solid var(--border)',
    }}>
      {titulo}
    </div>
  );
}

function Faixa({ rotulo, valor, destaque }: { rotulo: string; valor: string; destaque?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 10px', borderRadius: 5,
      background: destaque ? '#fde68a' : 'var(--panel-bg)',
      color: destaque ? '#000' : 'inherit',
      border: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.8 }}>{rotulo}</span>
      <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{valor}</span>
    </div>
  );
}

function Numero({ rotulo, valor, nota, destaque }: { rotulo: string; valor: string; nota?: string; destaque?: boolean }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        // Fio de acento a esquerda: da hierarquia sem inventar cor nova.
        borderLeft: `3px solid ${destaque ? '#000' : 'var(--primary)'}`,
        borderRadius: 6,
        padding: '10px 12px',
        // Destaque em amarelo+preto, nunca verde (daltonismo do Ruan).
        background: destaque ? '#fde68a' : 'var(--panel-bg)',
        color: destaque ? '#000' : 'inherit',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.75 }}>{rotulo}</div>
      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)', marginTop: 2 }}>{valor}</div>
      {nota && <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 2, lineHeight: 1.35 }}>{nota}</div>}
    </div>
  );
}
